import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { CirclePlus } from 'lucide-react';

import { Form } from '@/components/ui/form';
import { formSchema } from '../_validator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import SpinnerButton from '@/components/common/SpinnerButton';
import FormGeneral from './common/FormGeneral';
import Error from '@/components/common/Error';

import { usePostFee, useFees } from '@/data/fee';
import { FEE_TYPE } from '../enums';

const AddFeeDialog = () => {
  const [open, setIsOpen] = useState(false);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const postFee = usePostFee();
  const fees = useFees();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      label: '',
      value: '',
      type: FEE_TYPE.PERCENTAGE
    }
  });

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      setApiError('');
      const payload = {
        ...values,
        value: Math.round(parseFloat(values.value) * 100)
      };
      const fee = await postFee(payload);
      await fees.mutate((currentFees) => [...currentFees, fee], false);
      setSubmitting(false);
      setIsOpen(false);
    } catch (err) {
      setApiError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <CirclePlus className="mr-2 h-4 w-4" />
          Add Fee
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Fee</DialogTitle>
          <DialogDescription>Add a new global fee or rate</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormGeneral />
            {apiError && <Error error={apiError} />}
            <DialogFooter>
              <SpinnerButton loading={submitting}>Add Fee</SpinnerButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddFeeDialog;
